import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Leaf, HeartHandshake, ShieldCheck } from "lucide-react";
import { HERO, WELCOME, WHY_CHOOSE, ABOUT, IMAGES, SITE } from "@/data/content";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.img
            src={IMAGES.hero}
            alt="Calm sage green nature"
            className="w-full h-full object-cover"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 12, ease: "easeOut" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FAF8F4] via-[#FAF8F4]/90 to-[#FAF8F4]/50" />
        </div>

        {/* Animated decorative graphics */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          {/* Breathing gradient orbs */}
          <motion.div
            className="absolute top-[-6rem] right-[-4rem] w-96 h-96 rounded-full bg-[#6B8F71]/20 blur-3xl"
            animate={{ scale: [1, 1.18, 1], opacity: [0.5, 0.75, 0.5] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-[-5rem] left-[30%] w-80 h-80 rounded-full bg-[#C9A96A]/20 blur-3xl"
            animate={{ scale: [1, 1.25, 1], opacity: [0.35, 0.6, 0.35] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
          />

          {/* Floating leaves */}
          {[
            { top: "18%", left: "58%", size: 44, dur: 7, delay: 0, rot: 12 },
            { top: "60%", left: "72%", size: 30, dur: 9, delay: 1, rot: -18 },
            { top: "38%", left: "88%", size: 52, dur: 8, delay: 0.6, rot: 24 },
            { top: "78%", left: "50%", size: 26, dur: 6.5, delay: 1.8, rot: -8 },
          ].map((l, i) => (
            <motion.div
              key={i}
              className="absolute text-[#6B8F71]/40"
              style={{ top: l.top, left: l.left }}
              animate={{ y: [0, -22, 0], rotate: [l.rot, l.rot + 10, l.rot] }}
              transition={{ duration: l.dur, repeat: Infinity, ease: "easeInOut", delay: l.delay }}
            >
              <Leaf strokeWidth={1.25} style={{ width: l.size, height: l.size }} />
            </motion.div>
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-28 lg:pt-32 lg:pb-40">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.15 } } }}
            className="max-w-2xl"
          >
            <motion.p variants={fade} className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-6">
              {HERO.overline}
            </motion.p>
            <motion.h1 variants={fade} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.08] tracking-tight text-[#355C4A]">
              {HERO.title}
            </motion.h1>
            <motion.div variants={fade} className="mt-8 space-y-4 max-w-xl">
              {HERO.body.map((p, i) => (
                <p key={i} className="text-base lg:text-lg font-light leading-relaxed text-[#2F2F2F]/85">
                  {p}
                </p>
              ))}
            </motion.div>
            <motion.div variants={fade} className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-book-btn"
                className="group bg-[#6B8F71] text-white px-8 py-4 rounded-full hover:bg-[#355C4A] hover:-translate-y-0.5 transition-all duration-300 font-medium inline-flex items-center gap-2"
              >
                {HERO.cta}
                <ArrowRight strokeWidth={1.75} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="border border-[#6B8F71] text-[#355C4A] px-8 py-4 rounded-full hover:bg-[#6B8F71]/10 transition-all duration-300 font-medium"
              >
                Explore Services
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-[#6B8F71]"
          animate={{ y: [0, 8, 0], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        >
          <span className="text-[11px] tracking-[0.2em] uppercase">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-[#6B8F71] to-transparent" />
        </motion.div>
      </section>

      {/* Trust strip */}
      <section className="bg-[#355C4A]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center sm:text-left">
          {[
            { icon: ShieldCheck, t: "Confidential & Safe", d: "A judgment-free space" },
            { icon: HeartHandshake, t: "Warm & Client-Centred", d: "You are at the centre" },
            { icon: Leaf, t: "Online & In-Person", d: "Support that fits your life" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-4 justify-center sm:justify-start">
              <f.icon strokeWidth={1.5} className="w-7 h-7 text-[#C9A96A] shrink-0" />
              <div>
                <p className="text-white font-medium">{f.t}</p>
                <p className="text-[#C7D4C9] text-sm font-light">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Welcome */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fade}>
            <div className="relative">
              <div className="overflow-hidden rounded-[2rem] shadow-[0_8px_40px_rgba(107,143,113,0.15)]">
                <img src={IMAGES.room} alt="Tranquil counselling room" className="w-full h-[420px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-4 sm:right-6 bg-white rounded-2xl px-6 py-5 shadow-[0_8px_30px_rgba(107,143,113,0.18)] border border-[#6B8F71]/10 max-w-[220px]">
                <p className="font-serif text-3xl text-[#355C4A] leading-none">Courage</p>
                <p className="text-sm text-[#5A655F] mt-1 font-light">Seeking support is an act of courage.</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fade}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-5">{WELCOME.overline}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#355C4A]">{WELCOME.title}</h2>
            <div className="mt-6 space-y-4">
              {WELCOME.body.map((p, i) => (
                <p key={i} className="text-base lg:text-lg font-light leading-relaxed text-[#2F2F2F]/85">{p}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* About teaser */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fade} className="order-2 lg:order-1">
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-5">{ABOUT.intro.overline}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#355C4A]">{ABOUT.intro.title}</h2>
            <div className="mt-6 space-y-4">
              {ABOUT.intro.body.slice(0, 2).map((p, i) => (
                <p key={i} className="text-base lg:text-lg font-light leading-relaxed text-[#2F2F2F]/85">{p}</p>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="home-about-btn"
              className="mt-8 inline-flex items-center gap-2 text-[#355C4A] font-medium border-b border-[#C9A96A] pb-1 hover:gap-3 transition-all"
            >
              Read my full story <ArrowRight strokeWidth={1.75} className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fade} className="order-1 lg:order-2 flex justify-center">
            <div className="relative">
              <div className="w-72 h-72 sm:w-80 sm:h-80 rounded-full overflow-hidden border-8 border-[#FAF8F4] shadow-[0_8px_40px_rgba(107,143,113,0.2)]">
                <img src={IMAGES.portrait} alt={`${SITE.counsellor}, counsellor`} className="w-full h-full object-cover" />
              </div>
              <div className="absolute -z-10 inset-0 translate-x-6 translate-y-6 rounded-full bg-[#6B8F71]/15" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#6B8F71] mb-5">Why Choose New Beginning</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#355C4A]">A foundation of trust and care</h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_CHOOSE.map((item, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                variants={fade}
                data-testid={`why-item-${i}`}
                className="bg-white rounded-2xl p-6 border border-[#6B8F71]/10 shadow-[0_8px_30px_rgba(107,143,113,0.05)] hover:shadow-[0_8px_30px_rgba(107,143,113,0.12)] hover:-translate-y-1 transition-all duration-300"
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#6B8F71]/12 mb-4">
                  <Check strokeWidth={2} className="w-4 h-4 text-[#355C4A]" />
                </span>
                <p className="text-[#2F2F2F] font-medium leading-snug">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-[2rem] bg-[#6B8F71] px-8 py-16 sm:px-16 sm:py-20 text-center">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10" />
            <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-[#355C4A]/25" />
            <div className="relative">
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-white">Let's Begin Your Journey</h2>
              <p className="mt-4 text-white/90 font-light text-lg max-w-xl mx-auto">
                Taking the first step can feel difficult — but it can also be the beginning of meaningful change.
              </p>
              <Link
                to="/contact"
                data-testid="home-cta-btn"
                className="mt-8 inline-flex items-center gap-2 bg-white text-[#355C4A] px-8 py-4 rounded-full hover:-translate-y-0.5 transition-all duration-300 font-medium"
              >
                Book Your Appointment <ArrowRight strokeWidth={1.75} className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
